"use client";

import * as React from "react";
import { cn } from "@/lib/utils";
import { Check, X } from "lucide-react";

export type StepperV2Status = "pending" | "completed" | "current" | "rejected";

export interface StepperV2Step {
  id: string;
  title: string;
  description?: string;
  icon?: React.ReactNode;
  status: StepperV2Status;
}

export interface StepperV2Props {
  steps: StepperV2Step[];
  className?: string;
}

const PILL: Record<StepperV2Status, string> = {
  completed:
    "border-emerald-200/80 bg-emerald-50 text-emerald-700 dark:border-emerald-500/30 dark:bg-emerald-500/10 dark:text-emerald-300",
  current:
    "border-indigo-300 bg-white text-indigo-700 shadow-[0_6px_20px_-8px_rgba(79,70,229,0.5)] ring-1 ring-indigo-300/60 dark:border-indigo-400/40 dark:bg-zinc-900 dark:text-indigo-200 dark:ring-indigo-400/40",
  pending:
    "border-slate-200/80 bg-slate-50 text-slate-400 dark:border-zinc-800 dark:bg-zinc-900/60 dark:text-zinc-500",
  rejected:
    "border-rose-300 bg-rose-50 text-rose-700 dark:border-rose-500/30 dark:bg-rose-500/10 dark:text-rose-300",
};

const ICON_WRAP: Record<StepperV2Status, string> = {
  completed: "bg-emerald-500 text-white",
  current: "bg-indigo-500 text-white",
  pending: "bg-slate-200 text-slate-500 dark:bg-zinc-800 dark:text-zinc-400",
  rejected: "bg-rose-500 text-white",
};

const StepperV2 = React.forwardRef<HTMLDivElement, StepperV2Props>(
  ({ steps, className, ...props }, ref) => {
    return (
      <div
        ref={ref}
        className={cn("flex w-full flex-wrap items-center gap-y-2", className)}
        {...props}
      >
        {steps.map((step, index) => {
          const isLast = index === steps.length - 1;
          const leading =
            step.status === "completed" ? (
              <Check className="h-3 w-3" strokeWidth={3} />
            ) : step.status === "rejected" ? (
              <X className="h-3 w-3" strokeWidth={3} />
            ) : (
              <span className="flex h-3 w-3 items-center justify-center">
                {step.icon}
              </span>
            );

          return (
            <div key={step.id} className="flex items-center">
              <div
                className={cn(
                  "inline-flex items-center gap-2 rounded-2xl border px-3 py-2 transition-colors",
                  PILL[step.status]
                )}
              >
                <span
                  className={cn(
                    "flex h-5 w-5 shrink-0 items-center justify-center rounded-full",
                    ICON_WRAP[step.status]
                  )}
                >
                  {leading}
                </span>
                <span className="text-sm font-semibold leading-none">
                  {step.title}
                </span>
              </div>

              {!isLast && (
                <span
                  aria-hidden
                  className={cn(
                    "mx-2 h-[3px] w-5 shrink-0 rounded-full transition-colors",
                    step.status === "completed"
                      ? "bg-emerald-400/70 dark:bg-emerald-500/50"
                      : "bg-slate-200 dark:bg-zinc-700"
                  )}
                />
              )}
            </div>
          );
        })}
      </div>
    );
  }
);

StepperV2.displayName = "StepperV2";

export { StepperV2 };
